import {useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view, Button, Icon, tokens} from '@sail/ui';
import {accept, cancel} from '@sail/icons/react/Icon';
import {createKeyframes} from '@sail/engine';

const rotateGradient = createKeyframes({
  0: {transform: 'rotate(0deg)'},
  100: {transform: 'rotate(360deg)'},
});

type PlanItem = {
  uuid: string;
  step: string;
};

type AgentPlanCardProps = {
  title: string;
  items: PlanItem[];
  sendMessage: (messageText: string) => Promise<void>;
  loading?: boolean;
  isLastMessage: boolean;
};

const messages = defineMessages({
  confirm_action: {
    id: 'conversations_ui.agent_plan_card.confirm_action',
    description:
      'The text that shows near the actions buttons when not hovering over them',
    defaultMessage: 'Confirm action?',
  },
  yes_get_started: {
    id: 'conversations_ui.agent_plan_card.yes_get_started',
    description:
      'The text that shows when hovering over the "Yes, get started" button',
    defaultMessage: 'Yes, get started',
  },
  cancel_changes: {
    id: 'conversations_ui.agent_plan_card.cancel_changes',
    description:
      'The text that shows when hovering over the "No, I need to make changes" button',
    defaultMessage: 'No, I need to make changes',
  },
});

export const AgentPlanCard = ({
  title,
  items,
  sendMessage,
  loading = false,
  isLastMessage,
}: AgentPlanCardProps) => {
  const intl = useIntl();
  const [hoverText, setHoverText] = useState<string>(
    intl.formatMessage(messages.confirm_action),
  );

  const handleMouseEnter = (messageKey: keyof typeof messages) => {
    if (isLastMessage) {
      setHoverText(intl.formatMessage(messages[messageKey]));
    }
  };

  const handleMouseLeave = () => {
    if (isLastMessage) {
      setHoverText(intl.formatMessage(messages.confirm_action));
    }
  };

  return (
    <view.div css={{position: 'relative', maxWidth: '500px'}}>
      {loading ? (
        <view.div
          css={{
            position: 'absolute',
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            filter: 'blur(4px)',
          }}
        >
          <view.div
            css={{
              position: 'absolute',
              background:
                'conic-gradient(in oklch shorter hue from 180deg at 50% 50%, #80E9FF 0deg, #7A73FF 90deg, #80E9FF 180deg, #7A73FF 270deg, #80E9FF 360deg);',
              width: '500%',
              height: '500%',
              borderRadius: 'medium',
              animation: `${rotateGradient} 5s linear infinite`,
              opacity: 0.6,
              transformOrigin: 'center center',
            }}
          />
        </view.div>
      ) : null}
      <view.div
        css={{
          borderRadius: 'medium',
          padding: 'space.75',
          backgroundColor: 'offset',
          stack: 'y',
          gap: 'space.75',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <view.div
          css={{
            stack: 'y',
            gap: 'medium',
            maxWidth: '500px',
            position: 'relative',
            borderRadius: 'medium',
            padding: 'medium',
            borderStyle: 'solid',
            borderColor: tokens.color.neutral[50].toString(),
            borderWidth: 1,
            backgroundColor: 'surface',
          }}
        >
          <view.span css={{font: 'label.medium.emphasized'}}>{title}</view.span>
          {items.map((item) => (
            <view.div
              css={{
                stack: 'x',
                gap: 'small',
                alignItems: 'flex-start',
              }}
              key={item.uuid}
            >
              <view.div
                css={{
                  width: '12px',
                  height: '12px',
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: '1px dashed',
                  borderColor: tokens.color.neutral[300].toString(),
                  boxSizing: 'border-box',
                  display: 'inline-block',
                  marginTop: '2.5px',
                }}
              />
              <view.span css={{font: 'label.small'}}>{item.step}</view.span>
            </view.div>
          ))}
        </view.div>
        <view.div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 'space.75',
            paddingRight: 'space.1',
          }}
        >
          {hoverText}
        </view.div>
        <view.div css={{stack: 'x', gap: 'space.75'}}>
          <Button
            size="small"
            css={{width: 28, height: 24}}
            disabled={!isLastMessage}
            onMouseEnter={() => handleMouseEnter('yes_get_started')}
            onMouseLeave={handleMouseLeave}
            onClick={() =>
              sendMessage(intl.formatMessage(messages.yes_get_started))
            }
          >
            <Icon icon={accept} css={{height: 14}} />
          </Button>
          <Button
            size="small"
            css={{width: 28, height: 24}}
            disabled={!isLastMessage}
            onMouseEnter={() => handleMouseEnter('cancel_changes')}
            onMouseLeave={handleMouseLeave}
            onClick={() =>
              sendMessage(intl.formatMessage(messages.cancel_changes))
            }
          >
            <Icon icon={cancel} css={{height: 14}} />
          </Button>
        </view.div>
      </view.div>
    </view.div>
  );
};
